import { useId, useMemo } from 'react';
import { cn } from '@/lib/cn';
import { CLAIM_CODE_LENGTH, formatClaimCode, normalizeClaimCode } from '@/lib/claimCode';

interface ClaimCodeInputProps {
  /** Normalized value — no dashes. */
  value: string;
  onChange: (normalized: string) => void;
  label?: string;
  error?: string | undefined;
  disabled?: boolean;
}

export function ClaimCodeInput({
  value,
  onChange,
  label = 'Claim code',
  error,
  disabled,
}: ClaimCodeInputProps) {
  const id = useId();
  const complete = value.length === CLAIM_CODE_LENGTH;

  const display = useMemo(() => formatClaimCode(value), [value]);

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-[13px] font-medium text-ink-secondary">
        {label}
      </label>

      <input
        id={id}
        value={display}
        onChange={(event) => onChange(normalizeClaimCode(event.target.value))}
        disabled={disabled}
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        inputMode="text"
        placeholder="XXXX-XXXX-XXXX-XXXX-XXXX"
        aria-invalid={error ? true : undefined}
        aria-describedby={`${id}-hint`}
        className={cn(
          'w-full rounded-[--radius-control] border bg-surface px-3 py-2.5',
          'font-mono text-base tracking-[0.12em] text-ink placeholder:tracking-normal placeholder:text-ink-muted',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-accent-ring/50',
          error ? 'border-bad' : complete ? 'border-ok focus:border-ok' : 'border-line-strong focus:border-accent',
          disabled && 'bg-surface-sunken text-ink-muted'
        )}
      />

      <p id={`${id}-hint`} className={cn('text-[13px]', error ? 'text-bad' : 'text-ink-muted')}>
        {error ?? (
          <>
            {value.length} of {CLAIM_CODE_LENGTH} characters. Dashes and capitals are optional.
          </>
        )}
      </p>
    </div>
  );
}
